// src/api/client.ts
// THE ONE PLACE A REQUEST LEAVES THIS APP.
//
// Every call returns `Ok<T> | Refusal`. NOTHING here throws on a non-2xx, and nothing here throws on
// a transport failure — because a thrown error gets caught by whatever `try` happens to be nearest
// and rendered as "something went wrong", which is exactly the flattening `api/refusal.ts` exists to
// prevent. A refusal is a VALUE, so it survives being passed to a screen intact.
//
// THE TOKEN GOES TO THE CONFIGURED API BASE AND NOWHERE ELSE. `request()` builds its URL from
// `apiBase()` + a path this app wrote; it never takes a full URL from a server response. The one
// place this app fetches a URL the SERVER chose is the presigned part PUT in upload/multipart.ts,
// and that call deliberately sends NO Authorization header — see the note there.

import { apiBase } from '../config';
import { ensureFreshToken, refreshNow } from '../auth/session';
import { classify, unreachable, type Refusal } from './refusal';

export interface Ok<T> {
  readonly ok: true;
  readonly status: number;
  readonly value: T;
}

export type ApiResult<T> = Ok<T> | Refusal;

export interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  readonly body?: unknown;
  readonly query?: Record<string, string | number | undefined | null>;
  /** Send without a bearer token (used by nothing today; kept honest rather than assumed). */
  readonly anonymous?: boolean;
  readonly signal?: AbortSignal;
}

/** Injectable for tests. Defaults to the platform `fetch`. */
export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;
let fetchImpl: FetchLike | null = null;
export function setFetch(f: FetchLike | null): void { fetchImpl = f; }
function doFetch(url: string, init?: RequestInit): Promise<Response> {
  return (fetchImpl ?? globalThis.fetch)(url, init);
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const base = apiBase();
  let url = `${base}${path}`;
  if (query) {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === '') continue;
      params.set(k, String(v));
    }
    const qs = params.toString();
    if (qs) url += `?${qs}`;
  }
  return url;
}

/**
 * Parse a response body without ever letting a parse failure masquerade as a different error.
 *
 * A 503 from a load balancer arrives as HTML, not JSON. `res.json()` throws on it, and a client that
 * lets that throw propagate reports a SyntaxError where the truth was "the gateway is down". The
 * text is returned instead, and `classify` carries it as the message.
 */
async function readBody(res: Response): Promise<unknown> {
  const text = await res.text().catch(() => '');
  if (!text) return undefined;
  const type = res.headers.get('content-type') ?? '';
  if (type.includes('json') || text.startsWith('{') || text.startsWith('[')) {
    try { return JSON.parse(text) as unknown; } catch { return text; }
  }
  return text;
}

/**
 * @param retried - internal. Set only by the 401 path below, and the sole reason it exists is to
 *   bound the recursion at ONE. A "refresh then retry" that can call itself again is how a client
 *   with a dead credential turns into an infinite loop against the auth service — and it loops
 *   hardest exactly when the server is already unhappy. Not part of `RequestOptions`, because no
 *   caller outside this file has any business setting it.
 */
export async function request<T>(path: string, opts: RequestOptions = {}, retried = false): Promise<ApiResult<T>> {
  const method = opts.method ?? 'GET';
  const label = `${method} ${path}`;
  const url = buildUrl(path, opts.query);

  const headers: Record<string, string> = { accept: 'application/json' };
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  let sentToken: string | null = null;
  if (!opts.anonymous) {
    // PROACTIVE: renews the token if it is inside the expiry window, so the common case is that a
    // 401 never happens at all. The queue benefits from this without knowing about it — a job that
    // has been sitting in IndexedDB since before the credential rotated reads the CURRENT token
    // here, at send time, because no job ever stored one.
    sentToken = await ensureFreshToken();
    // No token is NOT a client-side refusal invented here — it is sent without one and the SERVER
    // answers 401. One authority, one answer; a local guess would drift from it.
    if (sentToken) headers['authorization'] = `Bearer ${sentToken}`;
  }

  let res: Response;
  try {
    res = await doFetch(url, {
      method,
      headers,
      ...(opts.body === undefined ? {} : { body: JSON.stringify(opts.body) }),
      ...(opts.signal ? { signal: opts.signal } : {}),
    });
  } catch (e) {
    const online = typeof navigator === 'undefined' ? null : navigator.onLine;
    return unreachable(label, e, online);
  }

  // ── REACTIVE: the server rejected the credential even though this device thought it was fine.
  //
  // That really happens, and not only through clock skew: a token can be revoked before its `exp`
  // (a password change, an admin sign-out), and this device has no way to know until it is told. One
  // refresh, one retry, then the 401 stands. If the refresh comes back `signedOut`, the credential
  // is definitively dead — the retry is skipped and the 401 is reported as what it is, so the app
  // shows a sign-in screen rather than hammering a route it cannot enter.
  if (res.status === 401 && !opts.anonymous && !retried && sentToken) {
    const refreshed = await refreshNow({ staleToken: sentToken });
    if (refreshed.ok && refreshed.session.token !== sentToken) {
      return request<T>(path, opts, true);
    }
  }

  const body = await readBody(res);
  if (!res.ok) return classify(res.status, body, label);
  return { ok: true, status: res.status, value: (body ?? {}) as T };
}

/** Narrowing helper so screens read as `if (isRefusal(r)) return renderRefusal(r)`. */
export function isRefusal<T>(r: ApiResult<T>): r is Refusal {
  return r.ok === false;
}
